import React from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const COUNTDOWN_SECONDS = 3;
const STORAGE_KEY = "app_version";
// Keys that should survive a forced reload so the user stays signed in.
const PRESERVE_KEYS = ["token", "auth_token", "user", "auth_user"];

/**
 * Performs a hard reload that:
 *   1. Preserves auth tokens (so user stays logged in)
 *   2. Wipes all other localStorage / sessionStorage entries
 *   3. Deletes any Service Worker caches (PWA / browser CacheStorage)
 *   4. Calls window.location.reload(true) with a cache-busting query param
 */
async function hardReloadPreservingAuth() {
  try {
    // 1. Snapshot auth-related keys
    const preserved = {};
    PRESERVE_KEYS.forEach((k) => {
      const v = localStorage.getItem(k);
      if (v !== null) preserved[k] = v;
    });

    // 2. Wipe storage
    try { localStorage.clear(); } catch (e) {}
    try { sessionStorage.clear(); } catch (e) {}

    // 3. Restore preserved keys
    Object.entries(preserved).forEach(([k, v]) => {
      try { localStorage.setItem(k, v); } catch (e) {}
    });

    // 4. Clear CacheStorage (Service Worker / PWA caches)
    if (typeof window !== "undefined" && "caches" in window) {
      try {
        const keys = await window.caches.keys();
        await Promise.all(keys.map((k) => window.caches.delete(k)));
      } catch (e) {}
    }

    // 5. Unregister service workers (forces fresh assets next load)
    if (typeof navigator !== "undefined" && navigator.serviceWorker) {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      } catch (e) {}
    }
  } catch (e) {
    // Never block the reload on a cleanup error.
  }

  // 6. Cache-busted reload
  const url = new URL(window.location.href);
  url.searchParams.set("_v", Date.now().toString());
  window.location.replace(url.toString());
}

/**
 * Shows a sonner toast with a live countdown + a "Reload now" button.
 * Auto-reloads after COUNTDOWN_SECONDS regardless of user interaction.
 */
function showUpdateToast() {
  let seconds = COUNTDOWN_SECONDS;
  let cancelled = false;

  const toastId = toast(
    `New version available — reloading in ${seconds}s…`,
    {
      duration: (COUNTDOWN_SECONDS + 1) * 1000,
      action: {
        label: "Reload now",
        onClick: () => {
          cancelled = true;
          hardReloadPreservingAuth();
        },
      },
    }
  );

  const interval = setInterval(() => {
    seconds -= 1;
    if (cancelled) {
      clearInterval(interval);
      return;
    }
    if (seconds <= 0) {
      clearInterval(interval);
      hardReloadPreservingAuth();
      return;
    }
    toast(`New version available — reloading in ${seconds}s…`, {
      id: toastId,
      duration: (seconds + 1) * 1000,
      action: {
        label: "Reload now",
        onClick: () => {
          cancelled = true;
          hardReloadPreservingAuth();
        },
      },
    });
  }, 1000);
}

/**
 * Mount-once component. Polls /api/version every 5 minutes and triggers a
 * cache-busting hard reload when the server version changes. Stores the last
 * seen version in localStorage so we don't notify on a fresh-load mismatch.
 */
export default function VersionCheck() {
  const triggeredRef = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;
    let timer = null;

    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/version`, {
          // bust browser cache on the version endpoint itself
          params: { t: Date.now() },
          timeout: 10000,
        });
        if (cancelled || !data || !data.version) return;

        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
          localStorage.setItem(STORAGE_KEY, data.version);
          return;
        }
        if (stored !== data.version && !triggeredRef.current) {
          triggeredRef.current = true;
          localStorage.setItem(STORAGE_KEY, data.version);
          showUpdateToast();
        }
      } catch (e) {
        // Network blip — try again next interval.
      }
    };

    // Run immediately so we capture the very first version on app load.
    check();
    timer = setInterval(check, POLL_INTERVAL_MS);

    // Re-check on tab focus / when the browser comes back from background.
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") check();
    });

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  return null;
}
